"use client";

import { Area, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { niceYTicks } from "@/lib/chart-ticks";
import type { SimResult } from "@/lib/simulator/types";

/* ──────────────────────────────────────────────────────────────────────────
 * SimulatorChart — workbench chart for `/simulate` + home hero.
 *
 * Two lines plus dynamic shading between them:
 *   - Portfolio value (solid forest green) — the brand color.
 *   - Loan balance (dashed gold) — the cost side.
 *   - Sage area between when portfolio is above loan (wealth zone).
 *   - Rust area between when portfolio is below loan (drawdown zone).
 *
 * The shading uses recharts' array-dataKey Area technique — same idiom as
 * the /learn Act 1 chart. Whitespace (undefined) values break the area at
 * crossover steps; an acceptable v1 compromise.
 *
 * For real-data scenarios the portfolio line shows actual market volatility;
 * the loan line shows monthly compounding stair-steps. The legend lives in
 * the parent component (the `/simulate` page renders a custom legend below
 * the chart so the chart itself stays uncluttered).
 * ────────────────────────────────────────────────────────────────────────── */

// Editorial palette — pale sage / desaturated rust at low opacity. Reference
// The Economist's gain/loss colors, not stock-app traffic-light tones.
const SAGE_FILL = "var(--color-nk-chart-wealth-fill)";
const RUST_FILL = "var(--color-nk-chart-drawdown-fill)";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(value);

/** Abbreviate large dollar values for Y-axis labels: $800K, $1.2M. */
function formatCurrencyAxis(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    const m = value / 1_000_000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    const k = value / 1_000;
    return `$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

const chartConfig = {
  portfolioValue: {
    label: "Portfolio",
    color: "var(--color-nk-brand)",
  },
  loanBalance: {
    label: "Loan balance",
    color: "var(--color-nk-accent)",
  },
} satisfies ChartConfig;

/** "2014-03-15" → 2014.21 — fractional year for evenly-spaced X axis ticks. */
function isoToYearFraction(isoDate: string): number {
  const [yearStr, monthStr, dayStr] = isoDate.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const dayOfYear = (month - 1) * 30.44 + day;
  return year + dayOfYear / 365.25;
}

export function SimulatorChart({ result }: { result: SimResult }) {
  const isRealData = result.scenario.stock.kind === "real";

  // Downsample for chart rendering — recharts can render thousands of points
  // but tick density and tooltip selection are smoother at ~weekly resolution
  // for daily data. Synthetic series are already small and pass through.
  const stride = result.series.length > 500 ? Math.ceil(result.series.length / 500) : 1;
  const data = result.series
    .filter((_, idx) => idx === 0 || idx === result.series.length - 1 || idx % stride === 0)
    .map((step) => {
      const portfolioValue = Math.round(step.portfolioValue);
      const loanBalance = Math.round(step.loanBalance);
      const aboveLoan = portfolioValue >= loanBalance;
      return {
        yearFraction: isoToYearFraction(step.date),
        date: step.date,
        portfolioValue,
        loanBalance,
        // Ranged areas: [low, high] tuple per data point. Recharts treats
        // undefined as a gap, which breaks the area at crossover steps —
        // accepted v1 compromise; smooth gradient transitions are deferred.
        wealthBand: aboveLoan ? ([loanBalance, portfolioValue] as [number, number]) : undefined,
        drawdownBand: aboveLoan ? undefined : ([portfolioValue, loanBalance] as [number, number]),
      };
    });

  // X-axis tick positions: one per year, at the integer year value.
  const firstYear = Math.floor(data[0]!.yearFraction);
  const lastYear = Math.ceil(data[data.length - 1]!.yearFraction);
  const yearTicks: number[] = [];
  for (let y = firstYear; y <= lastYear; y++) yearTicks.push(y);

  // Round-number Y-axis ticks so gridlines land on round dollar amounts.
  const yMin = Math.min(...data.map((d) => d.loanBalance));
  const yMax = Math.max(...data.map((d) => d.portfolioValue));
  const yTicks = niceYTicks(yMin, yMax);

  return (
    <ChartContainer config={chartConfig} className="aspect-[16/9] w-full md:aspect-[16/8]">
      <ComposedChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--color-nk-border)" strokeDasharray="2 4" />
        <XAxis
          dataKey="yearFraction"
          type="number"
          domain={[firstYear, lastYear]}
          ticks={yearTicks}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => String(Math.round(v))}
          tickMargin={8}
          stroke="var(--color-nk-foreground-muted)"
          style={{ fontSize: 11 }}
        />
        <YAxis
          ticks={yTicks}
          domain={[yTicks[0]!, yTicks[yTicks.length - 1]!]}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatCurrencyAxis}
          tickMargin={8}
          stroke="var(--color-nk-foreground-muted)"
          style={{ fontSize: 11, fontVariantNumeric: "tabular-nums" }}
          width={60}
        />
        <ChartTooltip
          cursor={{ stroke: "var(--color-nk-border-strong)", strokeWidth: 1 }}
          content={
            <ChartTooltipContent
              labelKey="date"
              labelFormatter={(
                label: unknown,
                payload: readonly { payload?: { date?: string } }[],
              ) => {
                const date = payload?.[0]?.payload?.date;
                if (!date) return String(label);
                return new Date(date).toLocaleDateString("en-CA", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                });
              }}
              labelClassName="font-display"
              formatter={(value, name) => {
                // Suppress band entries — they're visualization, not data points.
                if (name === "wealthBand" || name === "drawdownBand") return null;
                return (
                  <div className="flex w-full items-center justify-between gap-4">
                    <span className="text-nk-foreground-muted">
                      {name === "portfolioValue" ? "Portfolio" : "Loan balance"}
                    </span>
                    <span className="font-medium tabular-nums">
                      {formatCurrency(Number(value))}
                    </span>
                  </div>
                );
              }}
            />
          }
        />
        {/* Sage wealth band — drawn under the lines */}
        <Area
          type="monotone"
          dataKey="wealthBand"
          stroke="none"
          fill={SAGE_FILL}
          fillOpacity={0.55}
          isAnimationActive={false}
          activeDot={false}
          connectNulls={false}
        />
        {/* Rust drawdown band — drawn under the lines */}
        <Area
          type="monotone"
          dataKey="drawdownBand"
          stroke="none"
          fill={RUST_FILL}
          fillOpacity={0.65}
          isAnimationActive={false}
          activeDot={false}
          connectNulls={false}
        />
        <Line
          type="monotone"
          dataKey="portfolioValue"
          stroke="var(--color-nk-brand)"
          strokeWidth={isRealData ? 1.75 : 2.5}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          type="monotone"
          dataKey="loanBalance"
          stroke="var(--color-nk-accent)"
          strokeWidth={2}
          strokeDasharray="6 4"
          dot={false}
          activeDot={{ r: 4 }}
        />
      </ComposedChart>
    </ChartContainer>
  );
}
